import { useRef, useCallback, useEffect, useState, type ReactNode } from "react";
import type { WindowState, SnapZone } from "../../context/WindowManagerContext";
import { useWindowManager } from "../../context/WindowManagerContext";
import { useContextMenu, type ContextMenuItem } from "./ContextMenu";

interface WindowProps {
  state: WindowState;
  isActive: boolean;
  onFocus: () => void;
  onClose: () => void;
  onMinimize: () => void;
  onMaximize: () => void;
  onRestore: () => void;
  onMove: (pos: { x: number; y: number }) => void;
  onResize: (size: { width: number; height: number }) => void;
  onSnap: (zone: SnapZone) => void;
  onUnsnap: () => void;
  onDragCursorMove?: (pos: { x: number; y: number } | null) => void;
  children: ReactNode;
  /** Index used for staggered entrance animation */
  animationIndex?: number;
}

type ResizeDir = "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw";

const SNAP_EDGE_PX = 8;
const TASKBAR_HEIGHT = 48;
const MIN_VISIBLE_PX = 100;

function detectSnapZone(clientX: number, clientY: number): SnapZone {
  const w = window.innerWidth;
  const h = window.innerHeight - TASKBAR_HEIGHT;

  const atLeft = clientX <= SNAP_EDGE_PX;
  const atRight = clientX >= w - SNAP_EDGE_PX;
  const atTop = clientY <= SNAP_EDGE_PX;
  const atBottom = clientY >= h - SNAP_EDGE_PX;

  // Corners first
  if (atTop && atLeft) return "top-left";
  if (atTop && atRight) return "top-right";
  if (atBottom && atLeft) return "bottom-left";
  if (atBottom && atRight) return "bottom-right";

  // Edges
  if (atTop) return "top";
  if (atLeft) return "left";
  if (atRight) return "right";

  return null;
}

// Global counter for staggering window entrance animations
let windowInstanceCounter = 0;

export function Window({
  state,
  isActive,
  onFocus,
  onClose,
  onMinimize,
  onMaximize,
  onRestore,
  onMove,
  onResize,
  onSnap,
  onUnsnap,
  onDragCursorMove,
  children,
  animationIndex,
}: WindowProps) {
  const dragRef = useRef<{
    startX: number;
    startY: number;
    winX: number;
    winY: number;
    wasSnapped: boolean;
    preSnapSize: { width: number; height: number } | null;
  } | null>(null);
  const resizeRef = useRef<{
    dir: ResizeDir;
    startX: number;
    startY: number;
    origX: number;
    origY: number;
    origW: number;
    origH: number;
  } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const activeSnapZoneRef = useRef<SnapZone>(null);

  // Assign a stagger index on mount
  const [staggerIndex] = useState(() => {
    const idx = animationIndex ?? windowInstanceCounter;
    windowInstanceCounter++;
    return idx;
  });

  // --- Drag ---
  const onTitlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (state.status === "maximized") return;
      e.preventDefault();
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        winX: state.position.x,
        winY: state.position.y,
        wasSnapped: !!state.snappedZone,
        preSnapSize: state.preSnapGeometry?.size ?? null,
      };
      activeSnapZoneRef.current = null;
      setIsDragging(true);
      onFocus();
    },
    [state.position, state.status, state.snappedZone, state.preSnapGeometry, onFocus],
  );

  // --- Resize ---
  const onResizePointerDown = useCallback(
    (dir: ResizeDir, e: React.PointerEvent) => {
      if (state.status === "maximized") return;
      e.preventDefault();
      e.stopPropagation();
      resizeRef.current = {
        dir,
        startX: e.clientX,
        startY: e.clientY,
        origX: state.position.x,
        origY: state.position.y,
        origW: state.size.width,
        origH: state.size.height,
      };
      onFocus();
    },
    [state.position, state.size, state.status, onFocus],
  );

  useEffect(() => {
    const onPointerMove = (e: PointerEvent) => {
      if (dragRef.current) {
        const d = dragRef.current;
        const dx = e.clientX - d.startX;
        const dy = e.clientY - d.startY;

        // If window was snapped and user starts dragging, unsnap and restore original size
        if (d.wasSnapped && (Math.abs(dx) > 10 || Math.abs(dy) > 10)) {
          d.wasSnapped = false;
          if (d.preSnapSize) {
            // Center the restored window on the cursor
            const restoredW = d.preSnapSize.width;
            const restoredH = d.preSnapSize.height;
            d.winX = e.clientX - restoredW / 2;
            d.winY = e.clientY - 16; // offset from titlebar
            d.startX = e.clientX;
            d.startY = e.clientY;
            onResize({ width: restoredW, height: restoredH });
            onUnsnap();
          }
        }

        let newX = d.winX + dx;
        let newY = d.winY + dy;

        // Constrain: prevent dragging fully off-screen
        const winW = state.size.width;
        const winH = state.size.height;
        const viewW = window.innerWidth;
        const viewH = window.innerHeight - TASKBAR_HEIGHT;

        // At least MIN_VISIBLE_PX must remain visible
        newX = Math.max(-(winW - MIN_VISIBLE_PX), Math.min(viewW - MIN_VISIBLE_PX, newX));
        // Prevent dragging below taskbar
        newY = Math.max(-MIN_VISIBLE_PX, Math.min(viewH - MIN_VISIBLE_PX, newY));
        // Prevent going above viewport
        if (newY < 0) newY = 0;

        onMove({ x: newX, y: newY });

        // Detect snap zone
        const zone = detectSnapZone(e.clientX, e.clientY);
        activeSnapZoneRef.current = zone;
        onDragCursorMove?.({ x: e.clientX, y: e.clientY });
      }

      if (resizeRef.current) {
        const r = resizeRef.current;
        const dx = e.clientX - r.startX;
        const dy = e.clientY - r.startY;
        let { origX: x, origY: y, origW: w, origH: h } = r;

        if (r.dir.includes("e")) w = Math.max(320, r.origW + dx);
        if (r.dir.includes("w")) {
          const newW = Math.max(320, r.origW - dx);
          x = r.origX + (r.origW - newW);
          w = newW;
        }
        if (r.dir.includes("s")) h = Math.max(200, r.origH + dy);
        if (r.dir.includes("n")) {
          const newH = Math.max(200, r.origH - dy);
          y = r.origY + (r.origH - newH);
          h = newH;
        }

        onMove({ x, y });
        onResize({ width: w, height: h });
      }
    };

    const onPointerUp = () => {
      if (dragRef.current) {
        const zone = activeSnapZoneRef.current;
        if (zone) {
          onSnap(zone);
        }
        activeSnapZoneRef.current = null;
        onDragCursorMove?.(null);
        setIsDragging(false);
      }
      dragRef.current = null;
      resizeRef.current = null;
    };

    document.addEventListener("pointermove", onPointerMove);
    document.addEventListener("pointerup", onPointerUp);
    return () => {
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", onPointerUp);
    };
  }, [onMove, onResize, onSnap, onUnsnap, onDragCursorMove, state.size.width, state.size.height]);

  // Set grabbing cursor on document during drag
  useEffect(() => {
    if (isDragging) {
      document.body.style.cursor = "grabbing";
      document.body.style.userSelect = "none";
      return () => {
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };
    }
  }, [isDragging]);

  const handleDoubleClick = useCallback(() => {
    if (state.status === "maximized") {
      onRestore();
    } else {
      onMaximize();
    }
  }, [state.status, onRestore, onMaximize]);

  // Titlebar context menu
  const { snapWindow, closeAllWindows, setAlwaysOnTop } = useWindowManager();
  const titlebarCtx = useContextMenu();
  const [alwaysOnTop, setAlwaysOnTopLocal] = useState(false);

  const handleTitlebarContextMenu = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const items: ContextMenuItem[] = [
        { label: "Minimize", icon: "M3 19h18", action: () => onMinimize() },
        {
          label: state.status === "maximized" ? "Restore" : "Maximize",
          icon: state.status === "maximized"
            ? "M4 8V4h16v12h-4 M2 8h12v12H2z"
            : "M3 3h18v18H3z",
          action: () => (state.status === "maximized" ? onRestore() : onMaximize()),
        },
        { label: "Close", icon: "M18 6L6 18 M6 6l12 12", action: () => onClose() },
        "separator",
        {
          label: "Snap Left",
          icon: "M3 3h8v18H3z",
          action: () => snapWindow(state.id, "left"),
        },
        {
          label: "Snap Right",
          icon: "M13 3h8v18h-8z",
          action: () => snapWindow(state.id, "right"),
        },
        {
          label: "Always on Top",
          icon: "M12 2l2.4 7.2H22l-6 4.8 2.4 7.2L12 16l-6.4 5.2L8 14 2 9.2h7.6z",
          checked: alwaysOnTop,
          action: () => {
            const newVal = !alwaysOnTop;
            setAlwaysOnTopLocal(newVal);
            setAlwaysOnTop(state.id, newVal);
          },
        },
        "separator",
        {
          label: "Close All Windows",
          icon: "M18 6L6 18 M6 6l12 12",
          action: () => closeAllWindows(),
        },
      ];

      titlebarCtx.show(e, items);
    },
    [state, onMinimize, onMaximize, onRestore, onClose, snapWindow, closeAllWindows, setAlwaysOnTop, alwaysOnTop, titlebarCtx],
  );

  if (state.status === "minimized") {
    return null;
  }

  const cls = [
    "wm-window",
    isActive && "wm-window--active",
    !isActive && "wm-window--inactive",
    state.status === "maximized" && "wm-window--maximized",
    isDragging && "wm-window--dragging",
  ]
    .filter(Boolean)
    .join(" ");

  const style: React.CSSProperties =
    state.status === "maximized"
      ? { zIndex: state.zIndex }
      : {
          left: state.position.x,
          top: state.position.y,
          width: state.size.width,
          height: state.size.height,
          zIndex: state.zIndex,
          animationDelay: `${staggerIndex * 0.08}s`,
        };

  const resizeDirs: ResizeDir[] = ["n", "s", "e", "w", "ne", "nw", "se", "sw"];

  return (
    <div className={cls} style={style} onPointerDown={onFocus}>
      {titlebarCtx.render()}

      {/* Title bar */}
      <div
        className={`wm-titlebar ${!isActive ? "wm-titlebar--inactive" : ""}`}
        onPointerDown={onTitlePointerDown}
        onDoubleClick={handleDoubleClick}
        onContextMenu={handleTitlebarContextMenu}
      >
        <div className="wm-titlebar__icon">
          {state.icon && (
            <svg viewBox="0 0 24 24">
              <path d={state.icon} />
            </svg>
          )}
        </div>
        <div className="wm-titlebar__title">{state.title}</div>
        <div className="wm-titlebar__controls">
          <button
            className="wm-titlebar__btn wm-titlebar__btn--minimize"
            onClick={(e) => { e.stopPropagation(); onMinimize(); }}
          />
          <button
            className="wm-titlebar__btn wm-titlebar__btn--maximize"
            onClick={(e) => { e.stopPropagation(); state.status === "maximized" ? onRestore() : onMaximize(); }}
          />
          <button
            className="wm-titlebar__btn wm-titlebar__btn--close"
            onClick={(e) => { e.stopPropagation(); onClose(); }}
          />
        </div>
      </div>

      {/* Content */}
      <div className="wm-window__body">{children}</div>

      {/* Resize handles (not shown when maximized) */}
      {state.status !== "maximized" &&
        resizeDirs.map((dir) => (
          <div
            key={dir}
            className={`wm-resize wm-resize--${dir}`}
            onPointerDown={(e) => onResizePointerDown(dir, e)}
          />
        ))}
    </div>
  );
}
